import React, { useState, useEffect } from 'react';
import Header from '../components/ui/Header';
import AuthenticationGuard from '../components/ui/AuthenticationGuard';
import SmartLoadingBuffer from '../components/ui/SmartLoadingBuffer';
import Icon from '../components/AppIcon';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';

const SettingsPage = () => {
  const [settings, setSettings] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('notifications');

  // Mock settings data
  const mockSettings = {
    notifications: {
      habitReminders: true,
      studyReminders: true,
      achievementAlerts: true,
      weeklyReports: true,
      emailNotifications: false,
      pushNotifications: true
    },
    preferences: {
      theme: 'system',
      startWeek: 'monday',
      defaultStudyDuration: 25,
      hideCompletedHabits: false,
      showProgressInHeader: true,
      autoSyncData: true
    },
    privacy: {
      profileVisibility: 'private',
      shareProgress: false,
      allowAnalytics: true,
      dataExport: 'monthly'
    }
  };

  const settingSections = [
    { id: 'notifications', label: 'Notifications', icon: 'Bell' },
    { id: 'preferences', label: 'Preferences', icon: 'Settings' },
    { id: 'privacy', label: 'Privacy', icon: 'Shield' }
  ];

  useEffect(() => {
    // Simulate loading settings data
    const loadSettings = async () => {
      try {
        await new Promise(resolve => setTimeout(resolve, 800));
        setSettings(mockSettings);
        setIsLoading(false);
      } catch (error) {
        console.error('Error loading settings:', error);
        setIsLoading(false);
      }
    };

    loadSettings();
  }, []);

  const updateSetting = (section, key, value) => {
    setSettings(prevSettings => ({
      ...prevSettings,
      [section]: {
        ...prevSettings?.[section],
        [key]: value
      }
    }));
  };

  const renderNotificationSettings = () => (
    <div className="space-y-6">
      {Object.entries(settings?.notifications || {})?.map(([key, value]) => (
        <div key={key} className="flex items-center justify-between">
          <div>
            <h4 className="font-medium text-foreground capitalize">
              {key?.replace(/([A-Z])/g, ' $1')?.trim()}
            </h4>
            <p className="text-sm text-muted-foreground">
              {getNotificationDescription(key)}
            </p>
          </div>
          <button
            onClick={() => updateSetting('notifications', key, !value)}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              value ? 'bg-primary' : 'bg-muted'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                value ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
      ))}
    </div>
  );

  const renderPreferenceSettings = () => (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-foreground mb-2">
          Theme
        </label>
        <select
          className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
          value={settings?.preferences?.theme || ''}
          onChange={(e) => updateSetting('preferences', 'theme', e?.target?.value)}
        >
          <option value="light">Light</option>
          <option value="dark">Dark</option>
          <option value="system">System</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-foreground mb-2">
          Week Start Day
        </label>
        <select
          className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
          value={settings?.preferences?.startWeek || ''}
          onChange={(e) => updateSetting('preferences', 'startWeek', e?.target?.value)}
        >
          <option value="sunday">Sunday</option>
          <option value="monday">Monday</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-foreground mb-2">
          Default Study Duration (minutes)
        </label>
        <Input
          type="number"
          min="5"
          max="120"
          value={settings?.preferences?.defaultStudyDuration || 25}
          onChange={(e) => updateSetting('preferences', 'defaultStudyDuration', parseInt(e?.target?.value))}
        />
      </div>

      <div className="space-y-4">
        {Object.entries(settings?.preferences || {})?.filter(([key]) => typeof settings?.preferences?.[key] === 'boolean')?.map(([key, value]) => (
            <div key={key} className="flex items-center justify-between">
              <div>
                <h4 className="font-medium text-foreground capitalize">
                  {key?.replace(/([A-Z])/g, ' $1')?.trim()}
                </h4>
                <p className="text-sm text-muted-foreground">
                  {getPreferenceDescription(key)}
                </p>
              </div>
              <button
                onClick={() => updateSetting('preferences', key, !value)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  value ? 'bg-primary' : 'bg-muted'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    value ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          ))}
      </div>
    </div>
  );

  const renderPrivacySettings = () => (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-foreground mb-2">
          Profile Visibility
        </label>
        <select
          className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
          value={settings?.privacy?.profileVisibility || ''}
          onChange={(e) => updateSetting('privacy', 'profileVisibility', e?.target?.value)}
        >
          <option value="public">Public</option>
          <option value="friends">Friends Only</option>
          <option value="private">Private</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-foreground mb-2">
          Data Export Frequency
        </label>
        <select
          className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
          value={settings?.privacy?.dataExport || ''}
          onChange={(e) => updateSetting('privacy', 'dataExport', e?.target?.value)}
        >
          <option value="never">Never</option>
          <option value="monthly">Monthly</option>
          <option value="quarterly">Quarterly</option>
          <option value="yearly">Yearly</option>
        </select>
      </div>

      <div className="space-y-4">
        {Object.entries(settings?.privacy || {})?.filter(([key]) => typeof settings?.privacy?.[key] === 'boolean')?.map(([key, value]) => (
            <div key={key} className="flex items-center justify-between">
              <div>
                <h4 className="font-medium text-foreground capitalize">
                  {key?.replace(/([A-Z])/g, ' $1')?.trim()}
                </h4>
                <p className="text-sm text-muted-foreground">
                  {getPrivacyDescription(key)}
                </p>
              </div>
              <button
                onClick={() => updateSetting('privacy', key, !value)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  value ? 'bg-primary' : 'bg-muted'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    value ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          ))}
      </div>
    </div>
  );

  const getNotificationDescription = (key) => {
    const descriptions = {
      habitReminders: 'Get reminded about your daily habits',
      studyReminders: 'Receive study session notifications',
      achievementAlerts: 'Celebrate your achievements',
      weeklyReports: 'Weekly progress summaries',
      emailNotifications: 'Receive updates via email',
      pushNotifications: 'Browser push notifications'
    };
    return descriptions?.[key] || '';
  };

  const getPreferenceDescription = (key) => {
    const descriptions = {
      hideCompletedHabits: 'Hide completed habits from the main view',
      showProgressInHeader: 'Display progress indicator in navigation',
      autoSyncData: 'Automatically sync data across devices'
    };
    return descriptions?.[key] || '';
  };

  const getPrivacyDescription = (key) => {
    const descriptions = {
      shareProgress: 'Allow others to see your progress',
      allowAnalytics: 'Help improve the app with anonymous usage data'
    };
    return descriptions?.[key] || '';
  };

  if (isLoading) {
    return (
      <AuthenticationGuard isAuthenticated={true}>
        <div className="min-h-screen bg-background">
          <Header />
          <div className="pt-16">
            <SmartLoadingBuffer
              className="min-h-[60vh]"
              title="Loading your settings"
              message="Fetching profile, notification, and privacy preferences."
              hint="Cogito is applying your configuration."
            />
          </div>
        </div>
      </AuthenticationGuard>
    );
  }

  return (
    <AuthenticationGuard isAuthenticated={true}>
      <div className="min-h-screen bg-background">
        <Header />
        
        <main className="pt-16">
          <div className="max-w-4xl mx-auto px-4 lg:px-6 py-8">
            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-foreground mb-2">Settings</h1>
              <p className="text-muted-foreground">Customize your Cogito experience</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
              {/* Settings Navigation */}
              <div className="lg:col-span-1">
                <nav className="space-y-2">
                  {settingSections?.map((section) => (
                    <button
                      key={section?.id}
                      onClick={() => setActiveSection(section?.id)}
                      className={`flex items-center space-x-3 w-full p-3 text-left rounded-lg transition-colors ${
                        activeSection === section?.id
                          ? 'bg-primary text-primary-foreground'
                          : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                      }`}
                    >
                      <Icon name={section?.icon} size={20} />
                      <span className="font-medium">{section?.label}</span>
                    </button>
                  ))}
                </nav>
              </div>

              {/* Settings Content */}
              <div className="lg:col-span-3">
                <div className="bg-card rounded-xl border border-border p-6">
                  <h2 className="text-xl font-semibold text-foreground mb-6">
                    {settingSections?.find(s => s?.id === activeSection)?.label}
                  </h2>

                  {activeSection === 'notifications' && renderNotificationSettings()}
                  {activeSection === 'preferences' && renderPreferenceSettings()}
                  {activeSection === 'privacy' && renderPrivacySettings()}

                  <div className="mt-8 pt-6 border-t border-border">
                    <div className="flex items-center justify-between">
                      <Button variant="outline">
                        Reset to Defaults
                      </Button>
                      <Button>
                        Save Changes
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </AuthenticationGuard>
  );
};

export default SettingsPage;